import { Component, HostListener, Input, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { isEqual, pick } from "lodash";
import { forkJoin, of } from "rxjs";
import { map } from "rxjs/operators";
import { CameraComponent } from "src/app/annotation/camera/camera.component";
import { CanvasComponent } from "src/app/annotation/canvas/canvas.component";
import { TokenService } from "src/app/auth/token.service";
import { DeviceCheckService } from "src/app/core/survey/shared/services/device-check.service";
import { FormValidationService } from "src/app/core/survey/shared/services/formValidation.service";
import { NumericalService } from "src/app/core/survey/shared/services/numerical.service";
import { SurveyType } from "src/app/core/survey/shared/services/photo.service";
import { SavingStateService } from "src/app/core/survey/shared/services/saving-state.service";
import { OtherObstructionType } from "src/app/shared/Models/enums/OtherObstructionType";
import { StreetFurnitureType } from "src/app/shared/Models/enums/StreetFurnitureType";
import { StreetPavingTypeEnum } from "src/app/shared/Models/enums/StreetPavingTypeEnum";
import { SurveyPhotoTypeEnum } from "src/app/shared/Models/enums/SurveyPhotoTypeEnum";
import { SurveyStatus } from "src/app/shared/Models/enums/SurveySurveyStatus";
import { FacadeDetailPhotoRemarksDto } from "src/app/shared/Models/write/facadeDetailPhotoRemarks.model";
import { ToastService } from "src/app/shared/toast.service";
import { MainDistributionEnum } from "../../../../../../shared/Models/enums/MainDistributionEnum";
import { MainDistributionFrontYardPavement } from "../../../../../../shared/Models/enums/MainDistributionFrontYardPavement";
import { MainDistributionUndergroundReason } from "../../../../../../shared/Models/enums/MainDistributionUndergroundReason";
import { Building } from "../../../../../../shared/Models/read";
import {
  InsideSurveyOutsideInfo,
  INSIDE_SURVEY_OUTSIDE_TECHNICAL_INFO,
} from "../../../config/inside-survey.magic-strings";
import { InsideSurveyTab } from "../../../models/insideSurveyTab.interface";
import { OutsideTecnicalInfoMainDistributionType } from "../../../models/OutsideTecnicalInfoMainDistributionType.enum";
import { FrameListService } from "../../../services/frameList.service";
import { ValidationError } from "../../../../../../shared/Models/ValidationError";
import { PermissionsService } from "src/app/shared/services/permissions.service";
import { PermissionsEnum } from "src/app/shared/Models/enums/PermissionsEnum";
import { BuildingTypeEnum } from "src/app/shared/Models/enums/BuildingTypeEnum";

@Component({
  selector: "app-inside-survey-outside-technical-info",
  templateUrl: "./inside-survey-outside-technical-info.component.html",
  styleUrls: ["./inside-survey-outside-technical-info.component.scss"],
})
export class InsideSurveyOutsideTechnicalInfoComponent
  implements InsideSurveyTab, OnInit
{
  tabId: string = INSIDE_SURVEY_OUTSIDE_TECHNICAL_INFO;
  technicalInfoForms: { [key: string]: FormGroup } = {};
  mainDistributionEnum = MainDistributionEnum;
  surveyStatusEnum = SurveyStatus;
  surveyType = SurveyType;
  surveyPhotoType = SurveyPhotoTypeEnum;
  InsideSurveyOutsideInfoEnum = InsideSurveyOutsideInfo;

  @Input() viewerLayer: HTMLDivElement;
  @Input() cameraLayer: HTMLDivElement;
  @Input() camera: CameraComponent;

  outsideTechnicalInfoMainDistributionType =
    OutsideTecnicalInfoMainDistributionType;
  mainDistributionUndergroundReason = MainDistributionUndergroundReason;
  mainDistributionBoolOption = [
    { displayName: $localize`:@@general.yes:Yes`, value: true },
    { displayName: $localize`:@@general.no:No`, value: false },
  ];

  mainDistributionTypeOptions = [
    {
      displayName: $localize`:@@survey.inside.outside.technicalinfo.distrubution.type.underground:Underground`,
      value: OutsideTecnicalInfoMainDistributionType.Ondergronds,
    },
    {
      displayName: $localize`:@@survey.inside.outside.technicalinfo.distrubution.type.facade:Facade`,
      value: OutsideTecnicalInfoMainDistributionType.Gevel,
    },
  ];

  // facade questions

  patrimony = $localize`:@@survey.inside.outside.technicalinfo.facade.patrimony:Patrimony`;
  electricityCables = $localize`:@@survey.inside.outside.technicalinfo.facade.electricityCables:Presence of electricity cables`;
  coaxCables = $localize`:@@survey.inside.outside.technicalinfo.facade.coaxCables:Presence of coax cables`;
  multiTap = $localize`:@@survey.inside.outside.technicalinfo.facade.multiTap:Presence of multitabs`;
  publicLightning = $localize`:@@survey.inside.outside.technicalinfo.facade.publicLightning:Presence of public lighting`;
  solutionDoubt = $localize`:@@survey.outside.technical.facade.doubts: Façade Doubts`;

  // street questions
  streetFurniture = $localize`:@@survey.inside.outside.technicalinfo.street.streetFurniture:Street furniture`;
  otherObstruction = $localize`:@@survey.inside.outside.technicalinfo.street.otherObstruction:Facade obstacles`;

  streetFurnitureTypeOptions = [
    {
      value: StreetFurnitureType.Tree,
      label: $localize`:@@survey.inside.outside.technicalinfo.streetfurnituretype.tree:Tree`,
    },
    {
      value: StreetFurnitureType.StreetLight,
      label: $localize`:@@survey.inside.outside.technicalinfo.streetfurnituretype.lamppost:Lamppost`,
    },
    {
      value: StreetFurnitureType.Pole,
      label: $localize`:@@survey.inside.outside.technicalinfo.streetfurnituretype.pole:Pole`,
    },
    {
      value: StreetFurnitureType.ParkingMeter,
      label: $localize`:@@survey.inside.outside.technicalinfo.streetfurnituretype.parkingmeter:Parking meter`,
    },
    {
      value: StreetFurnitureType.RoadSign,
      label: $localize`:@@survey.inside.outside.technicalinfo.streetfurnituretype.roadsign:Road sign`,
    },
    {
      value: StreetFurnitureType.BusStop,
      label: $localize`:@@survey.inside.outside.technicalinfo.streetfurnituretype.busstop:Bus stop`,
    },
    {
      value: StreetFurnitureType.FootPath,
      label: $localize`:@@survey.inside.outside.technicalinfo.streetfurnituretype.footpath:Foot path`,
    },
    {
      value: StreetFurnitureType.Other,
      label: $localize`:@@survey.inside.outside.technicalinfo.streetfurnituretype.other:Other`,
    },
  ];
  otherObstructionTypeOptions = [
    {
      value: OtherObstructionType.Billboard,
      label: $localize`:@@survey.inside.outside.technicalinfo.otherobstructiontype.billboard:Billboard`,
    },
    {
      value: OtherObstructionType.CrepeWithInsulation,
      label: $localize`:@@survey.inside.outside.technicalinfo.otherobstructiontype.crepeWithInsulation:Crepe with insulation`,
    },
    {
      value: OtherObstructionType.Sunshade,
      label: $localize`:@@survey.inside.outside.technicalinfo.otherobstructiontype.sunshade:Sun shade`,
    },
    {
      value: OtherObstructionType.Terrace,
      label: $localize`:@@survey.inside.outside.technicalinfo.otherobstructiontype.terrace:Terrace`,
    },
    {
      value: OtherObstructionType.Lighting,
      label: $localize`:@@survey.inside.outside.technicalinfo.otherobstructiontype.lighting:Lighting`,
    },
    {
      value: OtherObstructionType.Other,
      label: $localize`:@@survey.inside.outside.technicalinfo.otherobstructiontype.other:Other`,
    },
  ];
  pavingFootpathTypeOptions = [
    {
      value: StreetPavingTypeEnum.FullGround,
      label: $localize`:@@enum.frontyard.pavement.fullground:Full Ground`,
    },
    {
      value: StreetPavingTypeEnum.StonePavement,
      label: $localize`:@@enum.frontyard.pavement.stonePavement:Stone Pavement`,
    },
    {
      value: StreetPavingTypeEnum.Asphalt,
      label: $localize`:@@enum.frontyard.pavement.asphalt:Asphalt`,
    },
    {
      value: StreetPavingTypeEnum.Concrete,
      label: $localize`:@@enum.frontyard.pavement.concrete:Concrete`,
    },
    {
      value: StreetPavingTypeEnum.OrnamentalPavement,
      label: $localize`:@@enum.frontyard.pavement.tiles.ornamental:Ornamental Pavement`,
    },
    {
      value: StreetPavingTypeEnum.Cobblestones,
      label: $localize`:@@enum.frontyard.pavement.tiles.cobblestones:Cobblestones`,
    },
    {
      value: StreetPavingTypeEnum.PebbleStone,
      label: $localize`:@@enum.frontyard.pavement.tiles.pebbleStone:Pebble-stone`,
    },
    {
      value: StreetPavingTypeEnum.Other,
      label: $localize`:@@enum.frontyard.pavement.other:Other`,
    },
  ];
  // distribution
  frontyard = $localize`:@@survey.inside.outside.technicalinfo.distribution.frontyard:Front yard`;

  mainDistributionUndergroundReasonOptions = [
    {
      value: MainDistributionUndergroundReason.Irregular,
      label: $localize`:@@survey.inside.outside.technicalinfo.distribution.undergroundReason.irregular:Irregular`,
    },
    {
      value: MainDistributionUndergroundReason.Monument,
      label: $localize`:@@survey.inside.outside.technicalinfo.distribution.undergroundReason.monument:Monument`,
    },
    {
      value: MainDistributionUndergroundReason.Glass,
      label: $localize`:@@survey.inside.outside.technicalinfo.distribution.undergroundReason.glass:Glass`,
    },
    {
      value: MainDistributionUndergroundReason.Gable,
      label: $localize`:@@survey.inside.outside.technicalinfo.distribution.undergroundReason.gable:Gable`,
    },
    {
      value: MainDistributionUndergroundReason.Height,
      label: $localize`:@@survey.inside.outside.technicalinfo.distribution.undergroundReason.height:Height`,
    },
    {
      value: MainDistributionUndergroundReason.Ornamental,
      label: $localize`:@@survey.inside.outside.technicalinfo.distribution.undergroundReason.ornamental:Ornamental`,
    },
  ];
  mainDistributionFrontYardPavementOptions = [
    {
      value: MainDistributionFrontYardPavement.FullGround,
      label: $localize`:@@enum.frontyard.pavement.fullground:Full Ground`,
    },
    {
      value: MainDistributionFrontYardPavement.StonePavement,
      label: $localize`:@@enum.frontyard.pavement.stonePavement:Stone Pavement`,
    },
    {
      value: MainDistributionFrontYardPavement.Asphalt,
      label: $localize`:@@enum.frontyard.pavement.asphalt:Asphalt`,
    },
    {
      value: MainDistributionFrontYardPavement.Concrete,
      label: $localize`:@@enum.frontyard.pavement.concrete:Concrete`,
    },
    {
      value: MainDistributionFrontYardPavement.OrnamentalPavement,
      label: $localize`:@@enum.frontyard.pavement.tiles.ornamental:Ornamental Pavement`,
    },
    {
      value: MainDistributionFrontYardPavement.Cobblestones,
      label: $localize`:@@enum.frontyard.pavement.tiles.cobblestones:Cobblestones`,
    },
    {
      value: MainDistributionFrontYardPavement.PebbleStone,
      label: $localize`:@@enum.frontyard.pavement.tiles.pebbleStone:Pebble-stone`,
    },
    {
      value: MainDistributionFrontYardPavement.Other,
      label: $localize`:@@enum.frontyard.pavement.other:Other`,
    },
  ];

  @Input() buildings: Building[];
  @Input() isAuthCanWrite: boolean;
  @Input() surveyStatus: SurveyStatus;

  inPortrait: boolean;

  constructor(
    private fb: FormBuilder,
    private toastService: ToastService,
    private tokenService: TokenService,
    private frameListService: FrameListService,
    private _deviceCheckService: DeviceCheckService,
    private _savingStateService: SavingStateService,
    private _numService: NumericalService, // is used in HTML Do Not remove,
    private formValidationService: FormValidationService,
    private permissionsService: PermissionsService
  ) {}

  ngOnInit() {
    this.inPortrait = this._deviceCheckService.onPortrait();
    this.buildForms();
    this.buildings.forEach((b) => {
      this.formValidationService.addFormError(
        [INSIDE_SURVEY_OUTSIDE_TECHNICAL_INFO, b.id],
        {
          [InsideSurveyOutsideInfo.HEAD_OF_DISTRIBUTION_SUBTITLE]: {
            [InsideSurveyOutsideInfo.MAIN_DISTRIBUTION_TYPE_FIELD]: false,
            [InsideSurveyOutsideInfo.FACADE_HEIGHT_FIELD]: false,
            [InsideSurveyOutsideInfo.UNDERGROUND_REASON_FIELD]: false,
            [InsideSurveyOutsideInfo.LENGTH_FRONT_YARD_FIELD]: false,
            [InsideSurveyOutsideInfo.FRONT_YARD_PAVEMENT_TYPE_FIELD]: false,
            [InsideSurveyOutsideInfo.FRONT_YARD_FIELD]: false,
          },
          [InsideSurveyOutsideInfo.FRONT_YARD_PICTURES_SUBTITLE]: {
            [InsideSurveyOutsideInfo.FRONT_YARD_PICTURES_FIELD]: false,
          },
          [InsideSurveyOutsideInfo.FACADE_SUBTITLE]: {
            [InsideSurveyOutsideInfo.PATRIMONY_FIELD]: false,
            [InsideSurveyOutsideInfo.ELECTRICITY_CABLES_FIELD]: false,
            [InsideSurveyOutsideInfo.COAX_CABLES_FIELD]: false,
            [InsideSurveyOutsideInfo.MULTITAP_FIELD]: false,
            [InsideSurveyOutsideInfo.PUBLIC_LIGHTING_FIELD]: false,
            [InsideSurveyOutsideInfo.FACADE_DOUBTS]: false,
            [InsideSurveyOutsideInfo.FACADE_DOUBTS_REMARK]: false,
          },
          [InsideSurveyOutsideInfo.STREET_SUBTITLE]: {
            [InsideSurveyOutsideInfo.STREET_FURNITURE_FIELD]: false,
            [InsideSurveyOutsideInfo.STREET_FURNITURE_QUESTION_FIELD]: false,
            [InsideSurveyOutsideInfo.OTHER_OBSTRUCTION_FIELD]: false,
            [InsideSurveyOutsideInfo.OTHER_OBSTRUCTION_QUESTION_FIELD]: false,
            [InsideSurveyOutsideInfo.PAVING_FOOTPATH_FIELD]: false,
          },
        }
      );
    });
  }

  @HostListener("window:resize")
  onResize() {
    this.inPortrait = this._deviceCheckService.onPortrait();
  }

  private buildForms() {
    this.buildings.forEach((b) => {
      this.technicalInfoForms[b.id] = this.fb.group({
        frontYard: [{ value: "", disabled: !this.isAuthCanWrite }, []],
        mainDistribution: [{ value: "", disabled: !this.isAuthCanWrite }, []],
        mainDistributionHeight: [
          { value: "", disabled: !this.isAuthCanWrite },
          [Validators.required, Validators.pattern("^[0-9]*$")],
        ],
        patrimony: [{ value: "", disabled: !this.isAuthCanWrite }, []],
        electricityCables: [{ value: "", disabled: !this.isAuthCanWrite }, []],
        coaxCables: [{ value: "", disabled: !this.isAuthCanWrite }, []],
        multiTap: [{ value: "", disabled: !this.isAuthCanWrite }, []],
        publicLightning: [{ value: "", disabled: !this.isAuthCanWrite }, []],
        solutionDoubt: [{ value: "", disabled: !this.isAuthCanWrite }, []],
        solutionDoubtRemark: [
          { value: "", disabled: !this.isAuthCanWrite },
          [],
        ],
        streetFurniture: [{ value: "", disabled: !this.isAuthCanWrite }, []],
        streetFurnitureTypes: [
          { value: "", disabled: !this.isAuthCanWrite },
          [],
        ],
        otherObstruction: [{ value: "", disabled: !this.isAuthCanWrite }, []],
        otherObstructionTypes: [
          { value: "", disabled: !this.isAuthCanWrite },
          [],
        ],
        pavingFootpath: [
          { value: "", disabled: !this.isAuthCanWrite },
          Validators.required,
          [],
        ],
        facadeRemark: [{ value: "", disabled: !this.isAuthCanWrite }, []],
        streetRemark: [{ value: "", disabled: !this.isAuthCanWrite }, []],
        generalBuildingRemark: [
          { value: "", disabled: !this.isAuthCanWrite },
          [],
        ],
        mainDistributionFrontYardLength: [
          { value: "", disabled: !this.isAuthCanWrite },
          [Validators.required, Validators.pattern("^[0-9]*$")],
        ],
        mainDistributionFrontYardPavement: [
          { value: "", disabled: !this.isAuthCanWrite },
          [],
        ],
        mainDistributionUndergroundReason: [
          { value: "", disabled: !this.isAuthCanWrite },
          [],
        ],
      });
      this.technicalInfoForms[b.id].patchValue(
        {
          ...b.outsideSurvey,
        },
        { emitEvent: false }
      );
    });
  }

  getMainDistributionType(key: string) {
    return this.technicalInfoForms[key].get("mainDistribution").value;
  }

  hasFrontYard(key: string) {
    return this.technicalInfoForms[key].get("frontYard").value;
  }

  hasSolutionDoubt(key: string) {
    return this.technicalInfoForms[key].get("solutionDoubt").value;
  }

  hasStreetFurniture(key: string) {
    return this.technicalInfoForms[key].get("streetFurniture").value;
  }

  hasOtherObstruction(key: string) {
    return this.technicalInfoForms[key].get("otherObstruction").value;
  }

  isValidSelection(key: string, control: string) {
    const form = this.technicalInfoForms[key];
    return (
      (form.get(control).touched || form.get(control).dirty) &&
      form.get(control).errors
    );
  }

  getErrors(building?: string, subTitle?: string, field?: string) {
    return this.formValidationService.getErrors(
      ...[
        INSIDE_SURVEY_OUTSIDE_TECHNICAL_INFO,
        building,
        subTitle,
        field,
      ].filter((x) => !!x)
    );
  }

  getValidationError(checkValidity?: boolean): ValidationError {
    if (checkValidity)
      this.formValidationService.resetErrorsForPath(
        INSIDE_SURVEY_OUTSIDE_TECHNICAL_INFO
      );

    var valError;
    Object.values(this.technicalInfoForms).forEach((f, index) => {
      const buildingId = Object.keys(this.technicalInfoForms)[index];
      if (
        f.get("mainDistribution").value == null ||
        f.get("mainDistribution").value === undefined
      )
        if (checkValidity)
          this.formValidationService.addError(
            INSIDE_SURVEY_OUTSIDE_TECHNICAL_INFO,
            buildingId,
            InsideSurveyOutsideInfo.HEAD_OF_DISTRIBUTION_SUBTITLE,
            InsideSurveyOutsideInfo.MAIN_DISTRIBUTION_TYPE_FIELD
          );

      if (
        f.get("mainDistribution").value ==
        OutsideTecnicalInfoMainDistributionType.Gevel
      ) {
        if (
          f.get("mainDistributionHeight").value == null ||
          f.get("mainDistributionHeight").value < 1
        )
          if (checkValidity)
            this.formValidationService.addError(
              INSIDE_SURVEY_OUTSIDE_TECHNICAL_INFO,
              buildingId,
              InsideSurveyOutsideInfo.HEAD_OF_DISTRIBUTION_SUBTITLE,
              InsideSurveyOutsideInfo.FACADE_HEIGHT_FIELD
            );

        f.controls.mainDistributionUndergroundReason.setValue(null);
      }
      if (
        f.get("mainDistribution").value ==
        OutsideTecnicalInfoMainDistributionType.Ondergronds
      ) {
        if (
          f.get("mainDistributionUndergroundReason").value == null ||
          f.get("mainDistributionHeight").value === undefined
        )
          if (checkValidity)
            this.formValidationService.addError(
              INSIDE_SURVEY_OUTSIDE_TECHNICAL_INFO,
              buildingId,
              InsideSurveyOutsideInfo.HEAD_OF_DISTRIBUTION_SUBTITLE,
              InsideSurveyOutsideInfo.UNDERGROUND_REASON_FIELD
            );

        f.controls.mainDistributionHeight.setValue(null);
      }
      if (
        f.get("frontYard").value == null ||
        f.get("frontYard").value == undefined
      )
        if (checkValidity)
          this.formValidationService.addError(
            INSIDE_SURVEY_OUTSIDE_TECHNICAL_INFO,
            buildingId,
            InsideSurveyOutsideInfo.HEAD_OF_DISTRIBUTION_SUBTITLE,
            InsideSurveyOutsideInfo.FRONT_YARD_FIELD
          );

      if (
        f.get("patrimony").value == null ||
        f.get("patrimony").value == undefined
      )
        if (checkValidity)
          this.formValidationService.addError(
            INSIDE_SURVEY_OUTSIDE_TECHNICAL_INFO,
            buildingId,
            InsideSurveyOutsideInfo.FACADE_SUBTITLE,
            InsideSurveyOutsideInfo.PATRIMONY_FIELD
          );

      if (
        f.get("electricityCables").value == null ||
        f.get("electricityCables").value == undefined
      )
        if (checkValidity)
          this.formValidationService.addError(
            INSIDE_SURVEY_OUTSIDE_TECHNICAL_INFO,
            buildingId,
            InsideSurveyOutsideInfo.FACADE_SUBTITLE,
            InsideSurveyOutsideInfo.ELECTRICITY_CABLES_FIELD
          );

      if (
        f.get("coaxCables").value == null ||
        f.get("coaxCables").value == undefined
      )
        if (checkValidity)
          this.formValidationService.addError(
            INSIDE_SURVEY_OUTSIDE_TECHNICAL_INFO,
            buildingId,
            InsideSurveyOutsideInfo.FACADE_SUBTITLE,
            InsideSurveyOutsideInfo.COAX_CABLES_FIELD
          );

      if (
        f.get("multiTap").value == null ||
        f.get("multiTap").value == undefined
      )
        if (checkValidity)
          this.formValidationService.addError(
            INSIDE_SURVEY_OUTSIDE_TECHNICAL_INFO,
            buildingId,
            InsideSurveyOutsideInfo.FACADE_SUBTITLE,
            InsideSurveyOutsideInfo.MULTITAP_FIELD
          );

      if (
        f.get("publicLightning").value == null ||
        f.get("publicLightning").value == undefined
      )
        if (checkValidity)
          this.formValidationService.addError(
            INSIDE_SURVEY_OUTSIDE_TECHNICAL_INFO,
            buildingId,
            InsideSurveyOutsideInfo.FACADE_SUBTITLE,
            InsideSurveyOutsideInfo.PUBLIC_LIGHTING_FIELD
          );

      if (
        f.get("solutionDoubt").value == null ||
        f.get("solutionDoubt").value == undefined
      ) {
        if (checkValidity)
          this.formValidationService.addError(
            INSIDE_SURVEY_OUTSIDE_TECHNICAL_INFO,
            buildingId,
            InsideSurveyOutsideInfo.FACADE_SUBTITLE,
            InsideSurveyOutsideInfo.FACADE_DOUBTS
          );
      } else if (
        checkValidity &&
        f.get("solutionDoubt").value &&
        !f.get("solutionDoubtRemark").value
      ) {
        this.formValidationService.addError(
          INSIDE_SURVEY_OUTSIDE_TECHNICAL_INFO,
          buildingId,
          InsideSurveyOutsideInfo.FACADE_SUBTITLE,
          InsideSurveyOutsideInfo.FACADE_DOUBTS_REMARK
        );
      }

      if (
        f.get("streetFurniture").value == null ||
        f.get("streetFurniture").value == undefined
      )
        if (checkValidity)
          this.formValidationService.addError(
            INSIDE_SURVEY_OUTSIDE_TECHNICAL_INFO,
            buildingId,
            InsideSurveyOutsideInfo.STREET_SUBTITLE,
            InsideSurveyOutsideInfo.STREET_FURNITURE_QUESTION_FIELD
          );

      if (
        f.get("streetFurniture").value &&
        !f.get("streetFurnitureTypes").value?.length
      )
        if (checkValidity)
          this.formValidationService.addError(
            INSIDE_SURVEY_OUTSIDE_TECHNICAL_INFO,
            buildingId,
            InsideSurveyOutsideInfo.STREET_SUBTITLE,
            InsideSurveyOutsideInfo.STREET_FURNITURE_FIELD
          );

      if (
        f.get("otherObstruction").value == null ||
        f.get("otherObstruction").value == undefined
      )
        if (checkValidity)
          this.formValidationService.addError(
            INSIDE_SURVEY_OUTSIDE_TECHNICAL_INFO,
            buildingId,
            InsideSurveyOutsideInfo.STREET_SUBTITLE,
            InsideSurveyOutsideInfo.OTHER_OBSTRUCTION_QUESTION_FIELD
          );

      if (
        f.get("otherObstruction").value &&
        !f.get("otherObstructionTypes").value?.length
      )
        if (checkValidity)
          this.formValidationService.addError(
            INSIDE_SURVEY_OUTSIDE_TECHNICAL_INFO,
            buildingId,
            InsideSurveyOutsideInfo.STREET_SUBTITLE,
            InsideSurveyOutsideInfo.OTHER_OBSTRUCTION_FIELD
          );

      if (
        f.get("pavingFootpath").value == null ||
        f.get("pavingFootpath").value == undefined
      )
        if (checkValidity)
          this.formValidationService.addError(
            INSIDE_SURVEY_OUTSIDE_TECHNICAL_INFO,
            buildingId,
            InsideSurveyOutsideInfo.STREET_SUBTITLE,
            InsideSurveyOutsideInfo.PAVING_FOOTPATH_FIELD
          );

      if (f.get("frontYard").value) {
        if (
          f.get("mainDistributionFrontYardLength").value == null ||
          f.get("mainDistributionFrontYardLength").value < 1
        )
          if (checkValidity)
            this.formValidationService.addError(
              INSIDE_SURVEY_OUTSIDE_TECHNICAL_INFO,
              buildingId,
              InsideSurveyOutsideInfo.HEAD_OF_DISTRIBUTION_SUBTITLE,
              InsideSurveyOutsideInfo.LENGTH_FRONT_YARD_FIELD
            );

        if (
          f.get("mainDistributionFrontYardPavement").value == null ||
          f.get("mainDistributionFrontYardPavement").value === undefined
        )
          if (checkValidity)
            this.formValidationService.addError(
              INSIDE_SURVEY_OUTSIDE_TECHNICAL_INFO,
              buildingId,
              InsideSurveyOutsideInfo.HEAD_OF_DISTRIBUTION_SUBTITLE,
              InsideSurveyOutsideInfo.FRONT_YARD_PAVEMENT_TYPE_FIELD
            );

        if (
          !this.buildings.find((e) => e.id == buildingId).outsideSurvey.photos
            .frontYardPhotos.length
        )
          if (checkValidity)
            this.formValidationService.addError(
              INSIDE_SURVEY_OUTSIDE_TECHNICAL_INFO,
              buildingId,
              InsideSurveyOutsideInfo.FRONT_YARD_PICTURES_SUBTITLE,
              InsideSurveyOutsideInfo.FRONT_YARD_PICTURES_FIELD
            );
      } else {
        f.controls.mainDistributionFrontYardLength.setValue(null);
        f.controls.mainDistributionFrontYardPavement.setValue(null);
      }
      //RETURN ERRORS INDIVIDUALLY
      if (
        this.getErrors(
          buildingId,
          InsideSurveyOutsideInfo.HEAD_OF_DISTRIBUTION_SUBTITLE,
          InsideSurveyOutsideInfo.FACADE_HEIGHT_FIELD
        )
      )
        valError = new ValidationError(
          $localize`:@@validaton.fieldisrequired:Field is required`,
          $localize`:@@survey.inside.outside.technicalinfo.distribution.validation.facedeHight:Facade height not added`,
          $localize`:@@survey.inside.technicalinfo.outside:Outside technical info`
        );

      if (
        this.getErrors(
          buildingId,
          InsideSurveyOutsideInfo.HEAD_OF_DISTRIBUTION_SUBTITLE,
          InsideSurveyOutsideInfo.MAIN_DISTRIBUTION_TYPE_FIELD
        )
      )
        valError = new ValidationError(
          $localize`:@@validaton.fieldisrequired:Field is required`,
          $localize`:@@survey.inside.outside.technicalinfo.distribution.validation.mainDistributionTypeField:MainDistributionType has no value`,
          $localize`:@@survey.inside.technicalinfo.outside:Outside technical info`
        );

      if (
        this.getErrors(
          buildingId,
          InsideSurveyOutsideInfo.HEAD_OF_DISTRIBUTION_SUBTITLE,
          InsideSurveyOutsideInfo.UNDERGROUND_REASON_FIELD
        )
      )
        valError = new ValidationError(
          $localize`:@@validaton.fieldisrequired:Field is required`,
          $localize`:@@survey.inside.outside.technicalinfo.distribution.validation.undergroundReason:Underground reason not added!`,
          $localize`:@@survey.inside.technicalinfo.outside:Outside technical info`
        );

      if (
        this.getErrors(
          buildingId,
          InsideSurveyOutsideInfo.HEAD_OF_DISTRIBUTION_SUBTITLE,
          InsideSurveyOutsideInfo.LENGTH_FRONT_YARD_FIELD
        )
      )
        valError = new ValidationError(
          $localize`:@@validaton.fieldisrequired:Field is required`,
          $localize`:@@survey.inside.outside.technicalinfo.distribution.validation.frontyardLength:Length front yard not added!`,
          $localize`:@@survey.inside.technicalinfo.outside:Outside technical info`
        );

      if (
        this.getErrors(
          buildingId,
          InsideSurveyOutsideInfo.HEAD_OF_DISTRIBUTION_SUBTITLE,
          InsideSurveyOutsideInfo.FRONT_YARD_PAVEMENT_TYPE_FIELD
        )
      )
        valError = new ValidationError(
          $localize`:@@validaton.fieldisrequired:Field is required`,
          $localize`:@@survey.inside.outside.technicalinfo.distribution.validation.pavementNotAdded:Pavement not added!`,
          $localize`:@@survey.inside.technicalinfo.outside:Outside technical info`
        );

      if (
        this.getErrors(
          buildingId,
          InsideSurveyOutsideInfo.FACADE_SUBTITLE,
          InsideSurveyOutsideInfo.FACADE_DOUBTS
        )
      )
        valError = new ValidationError(
          $localize`:@@validaton.fieldisrequired:Field is required`,
          $localize`:@@survey.inside.outside.technicalinfo.distribution.validation.facadeDoubtsNotAdded:Facade doubts not added!`,
          $localize`:@@survey.inside.technicalinfo.outside:Outside technical info`
        );

      if (
        this.getErrors(
          buildingId,
          InsideSurveyOutsideInfo.FACADE_SUBTITLE,
          InsideSurveyOutsideInfo.FACADE_DOUBTS_REMARK
        )
      )
        valError = new ValidationError(
          $localize`:@@validaton.fieldisrequired:Field is required`,
          $localize`:@@survey.inside.outside.technicalinfo.distribution.validation.facadeDoubtsRemarkNotAdded:Facade doubts remark not added!`,
          $localize`:@@survey.inside.technicalinfo.outside:Outside technical info`
        );

      if (
        this.getErrors(
          buildingId,
          InsideSurveyOutsideInfo.FRONT_YARD_PICTURES_SUBTITLE,
          InsideSurveyOutsideInfo.FRONT_YARD_PICTURES_FIELD
        )
      )
        valError = new ValidationError(
          $localize`:@@validaton.fieldisrequired:Field is required`,
          $localize`:@@survey.inside.outside.technicalinfo.frontyard.pictures.required:Upload atleast 1 frontyard picture!`,
          $localize`:@@survey.inside.technicalinfo.outside:Outside technical info`
        );

      if (
        this.getErrors(
          buildingId,
          InsideSurveyOutsideInfo.STREET_SUBTITLE,
          InsideSurveyOutsideInfo.STREET_FURNITURE_QUESTION_FIELD
        )
      )
        valError = new ValidationError(
          $localize`:@@validaton.fieldisrequired:Field is required`,
          $localize`:@@survey.inside.outside.technicalinfo.street.furniture.question.required:Street furniture question was not answered!`,
          $localize`:@@survey.inside.technicalinfo.outside:Outside technical info`
        );

      if (
        this.getErrors(
          buildingId,
          InsideSurveyOutsideInfo.STREET_SUBTITLE,
          InsideSurveyOutsideInfo.STREET_FURNITURE_FIELD
        )
      )
        valError = new ValidationError(
          $localize`:@@validaton.fieldisrequired:Field is required`,
          $localize`:@@survey.inside.outside.technicalinfo.street.furniture.selection.required:No street furniture was selected!`,
          $localize`:@@survey.inside.technicalinfo.outside:Outside technical info`
        );

      if (
        this.getErrors(
          buildingId,
          InsideSurveyOutsideInfo.STREET_SUBTITLE,
          InsideSurveyOutsideInfo.OTHER_OBSTRUCTION_QUESTION_FIELD
        )
      )
        valError = new ValidationError(
          $localize`:@@validaton.fieldisrequired:Field is required`,
          $localize`:@@survey.inside.outside.technicalinfo.other.obstruction.question.required:Other obstruction question was not answered!`,
          $localize`:@@survey.inside.technicalinfo.outside:Outside technical info`
        );

      if (
        this.getErrors(
          buildingId,
          InsideSurveyOutsideInfo.STREET_SUBTITLE,
          InsideSurveyOutsideInfo.OTHER_OBSTRUCTION_FIELD
        )
      )
        valError = new ValidationError(
          $localize`:@@validaton.fieldisrequired:Field is required`,
          $localize`:@@survey.inside.outside.technicalinfo.other.obstruction.selection.required:No other obstruction was selected!`,
          $localize`:@@survey.inside.technicalinfo.outside:Outside technical info`
        );
    });

    return valError;
  }

  get isMainDistributionEditable(): boolean {
    return (
      this.isAuthCanWrite &&
      this.permissionsService.allowed(PermissionsEnum.EditMainDistribution)
    );
  }

  save(checkValidity?: boolean) {
    Object.values(this.technicalInfoForms).forEach((f, index) => {
      // clear related fields of explicitly set to false
      if (
        f.get("frontYard").value !== null &&
        f.get("frontYard").value !== undefined &&
        !f.get("frontYard").value
      ) {
        f.controls.mainDistributionFrontYardLength.setValue(null);
        f.controls.mainDistributionFrontYardPavement.setValue(null);
      }
    });

    const keysToUpdate = this.detectChanges();
    if (keysToUpdate.length == 0) return of(true);

    if (checkValidity) {
      const error = this.getValidationError();
      if (error) {
        this.toastService.showWarnToast(error.title, error.message);
        return of(false);
      }
    }

    return forkJoin(
      keysToUpdate.map((k) => {
        const index = this.buildings.findIndex((b) => b.id == k);
        const thisBuilding = this.buildings[index];
        const thisBuildingForm = this.technicalInfoForms[k];

        let photoRemarks = [];

        var remarks = thisBuilding.outsideSurvey.photos.frontYardPhotos
          .filter((p) => p.remarkChanged)
          .concat(
            thisBuilding.outsideSurvey.photos.solutionDoubtPhotos.filter(
              (p) => p.remarkChanged
            )
          )
          .map((f) => {
            f.remarkChanged = false;
            return new FacadeDetailPhotoRemarksDto(
              f.data.id,
              f.facadeDetailPhotoRemark
            );
          });

        photoRemarks = photoRemarks.concat(remarks);

        thisBuilding.outsideSurvey = {
          ...thisBuilding.outsideSurvey,
          ...thisBuildingForm.value,
        };

        return this._savingStateService.addInsideOutsideSurveyTechnicalInfo({
          id: thisBuilding.outsideSurvey.id,
          frontYardPhotoRemarks: photoRemarks,
          ...thisBuildingForm.value,
        });
      })
    ).pipe(map((val) => val.every((v) => !!v)));
  }

  private detectChanges() {
    const keysToUpdate = [];
    Object.keys(this.technicalInfoForms).forEach((k) => {
      const formDefinition = Object.keys(this.technicalInfoForms[k].controls);
      const thisBuilding = this.buildings.find((b) => b.id == k);
      const thisBuildingFrom = this.technicalInfoForms[k];

      if (
        !isEqual(
          thisBuildingFrom.value,
          pick(thisBuilding.outsideSurvey, formDefinition)
        ) ||
        this.buildings.some(
          (b) =>
            b.outsideSurvey.photos.frontYardPhotos.some(
              (p) => p.remarkChanged
            ) ||
            b.outsideSurvey.photos.solutionDoubtPhotos.some(
              (p) => p.remarkChanged
            )
        )
      )
        keysToUpdate.push(k);
    });
    return keysToUpdate;
  }

  get showOpenButton$() {
    return this.frameListService
      .anyOpen(...this.buildings.map((b) => b.id))
      .pipe(map((b) => !b));
  }

  openAllFrames() {
    this.frameListService.open(...this.buildings.map((b) => b.id));
  }
  closeAllFrames() {
    this.frameListService.close(...this.buildings.map((b) => b.id));
  }
}
